
// components/Payments.jsx
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import { 
  Plus, Trash2, ArrowLeft, Edit2, Save, X, 
  Users, DollarSign, Calendar, CreditCard, RefreshCw,
  Wallet, Building, Banknote, QrCode
} from 'lucide-react';
import { usePayments } from '../hooks/usePayments';
import { useCustomers } from '../hooks/useCustomers';
import { PAYMENT_TYPES } from '../utils/constants';

const Payments = () => {
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [formData, setFormData] = useState({
    customer_id: '',
    payment_type: 'cash',
    amount_paid: '',
    total_amount: '',
    payment_date: ''
  });
  
  const navigate = useNavigate();
  const { payments, loading, addPayment, updatePayment, deletePayment } = usePayments();
  const { customers } = useCustomers();

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validation
    if (!formData.customer_id) {
      toast.error('Please select a customer');
      return;
    }
    if (!formData.total_amount || parseFloat(formData.total_amount) <= 0) {
      toast.error('Total amount must be greater than 0');
      return;
    }
    if (!formData.amount_paid || parseFloat(formData.amount_paid) <= 0) {
      toast.error('Amount paid must be greater than 0');
      return;
    }
    if (parseFloat(formData.amount_paid) > parseFloat(formData.total_amount)) {
      toast.error('Amount paid cannot exceed total amount');
      return;
    }
    if (!formData.payment_date) {
      toast.error('Please select payment date');
      return;
    }
    
    const paymentData = {
      customer_id: parseInt(formData.customer_id),
      payment_type: formData.payment_type,
      amount_paid: parseFloat(formData.amount_paid),
      total_amount: parseFloat(formData.total_amount),
      payment_date: formData.payment_date
    };

    try {
      if (editingId) {
        await updatePayment(editingId, paymentData);
      } else {
        await addPayment(paymentData);
      }
      
      setShowForm(false);
      setEditingId(null);
      setFormData({ 
        customer_id: '', 
        payment_type: 'cash', 
        amount_paid: '', 
        total_amount: '',
        payment_date: '' 
      });
    } catch (error) {
      // Error already handled in hook
    }
  };

  const handleEdit = (payment) => {
    setEditingId(payment.id);
    setFormData({
      customer_id: payment.customer_id,
      payment_type: payment.payment_type,
      amount_paid: payment.amount_paid,
      total_amount: payment.total_amount,
      payment_date: payment.payment_date
    });
    setShowForm(true);
  };

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this payment?')) {
      await deletePayment(id);
    }
  };

  const handleCancel = () => {
    setShowForm(false);
    setEditingId(null);
    setFormData({ 
      customer_id: '', 
      payment_type: 'cash', 
      amount_paid: '', 
      total_amount: '',
      payment_date: '' 
    });
  };

  // Get payment type icon
  const getPaymentTypeIcon = (type) => {
    switch(type) {
      case 'cash': return <Banknote className="w-4 h-4" />;
      case 'online': return <QrCode className="w-4 h-4" />;
      case 'bank_transfer': return <Building className="w-4 h-4" />;
      case 'cheque': return <Wallet className="w-4 h-4" />;
      default: return <CreditCard className="w-4 h-4" />;
    }
  };

  // Get payment type color
  const getPaymentTypeColor = (type) => {
    switch(type) {
      case 'cash': return 'bg-green-100 text-green-800';
      case 'online': return 'bg-blue-100 text-blue-800';
      case 'bank_transfer': return 'bg-purple-100 text-purple-800';
      case 'cheque': return 'bg-yellow-100 text-yellow-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading payments...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Navbar */}
      <nav className="bg-gradient-to-r from-green-700 to-green-800 text-white p-4 shadow-lg sticky top-0 z-50">
        <div className="container mx-auto flex justify-between items-center flex-wrap gap-2">
          <div className="flex items-center gap-4">
            <button 
              onClick={() => navigate('/dashboard')} 
              className="text-white hover:text-gray-200 transition flex items-center"
            >
              <ArrowLeft className="inline-block mr-2" size={20} />
              Back
            </button>
            <h1 className="text-2xl font-bold">💳 Manage Payments</h1>
          </div>
          <button
            onClick={() => {
              setShowForm(!showForm);
              setEditingId(null);
              setFormData({ 
                customer_id: '', 
                payment_type: 'cash', 
                amount_paid: '', 
                total_amount: '',
                payment_date: '' 
              });
            }}
            className="bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-md transition flex items-center"
          >
            <Plus className="mr-2" size={18} />
            Add Payment
          </button>
        </div>
      </nav>

      <div className="container mx-auto p-4 md:p-6">
        {/* Payment Form */}
        {showForm && (
          <div className="bg-white p-6 rounded-lg shadow-md mb-6 border-l-4 border-green-500">
            <h2 className="text-xl font-bold text-gray-800 mb-4 flex items-center">
              {editingId ? (
                <>
                  <Edit2 className="mr-2 text-green-600" size={20} />
                  Edit Payment
                </>
              ) : (
                <>
                  <Plus className="mr-2 text-green-600" size={20} />
                  Add New Payment
                </>
              )}
            </h2>
            <form onSubmit={handleSubmit} className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* Customer Selection */}
              <div>
                <label className="block text-gray-700 text-sm font-semibold mb-1 flex items-center gap-1">
                  <Users size={16} className="text-green-600" />
                  Select Customer <span className="text-red-500">*</span>
                </label>
                <select
                  className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-green-500 text-gray-800"
                  value={formData.customer_id}
                  onChange={(e) => setFormData({...formData, customer_id: e.target.value})}
                  required
                >
                  <option value="">-- Choose a customer --</option>
                  {customers.map((customer) => (
                    <option key={customer.id} value={customer.id}>
                      {customer.customer_name} - {customer.consumer_number}
                    </option>
                  ))}
                </select>
                <p className="text-xs text-gray-400 mt-1">Select the customer making payment</p>
              </div>

              {/* Payment Type */}
              <div>
                <label className="block text-gray-700 text-sm font-semibold mb-1 flex items-center gap-1">
                  <CreditCard size={16} className="text-green-600" />
                  Payment Type <span className="text-red-500">*</span>
                </label>
                <select
                  className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-green-500 text-gray-800"
                  value={formData.payment_type}
                  onChange={(e) => setFormData({...formData, payment_type: e.target.value})}
                  required
                >
                  {PAYMENT_TYPES.map((type) => (
                    <option key={type.value} value={type.value}>
                      {type.label}
                    </option>
                  ))}
                </select>
                <p className="text-xs text-gray-400 mt-1">Select payment method</p>
              </div>

              {/* Total Amount */}
              <div>
                <label className="block text-gray-700 text-sm font-semibold mb-1 flex items-center gap-1">
                  <DollarSign size={16} className="text-green-600" />
                  Total Amount <span className="text-red-500">*</span>
                </label>
                <input
                  type="number"
                  placeholder="Enter total amount (e.g., 1000)"
                  className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-green-500 text-gray-800"
                  value={formData.total_amount}
                  onChange={(e) => setFormData({...formData, total_amount: e.target.value})}
                  required
                  step="0.01"
                  min="0.01"
                />
                <p className="text-xs text-gray-400 mt-1">Total bill amount for this payment</p>
              </div>

              {/* Amount Paid */}
              <div>
                <label className="block text-gray-700 text-sm font-semibold mb-1 flex items-center gap-1">
                  <Wallet size={16} className="text-green-600" />
                  Amount Paid <span className="text-red-500">*</span>
                </label>
                <input
                  type="number"
                  placeholder="Enter amount paid (e.g., 500)"
                  className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-green-500 text-gray-800"
                  value={formData.amount_paid}
                  onChange={(e) => setFormData({...formData, amount_paid: e.target.value})}
                  required
                  step="0.01"
                  min="0.01"
                />
                <p className="text-xs text-gray-400 mt-1">Amount actually paid by customer</p>
              </div>

              {/* Payment Date */}
              <div className="md:col-span-2">
                <label className="block text-gray-700 text-sm font-semibold mb-1 flex items-center gap-1">
                  <Calendar size={16} className="text-green-600" />
                  Payment Date <span className="text-red-500">*</span>
                </label>
                <input
                  type="date"
                  className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-green-500 text-gray-800"
                  value={formData.payment_date}
                  onChange={(e) => setFormData({...formData, payment_date: e.target.value})}
                  required
                />
                <p className="text-xs text-gray-400 mt-1">Date when payment was made</p>
              </div>

              <div className="md:col-span-2 flex gap-4">
                <button 
                  type="submit" 
                  className="bg-green-600 hover:bg-green-700 text-white px-6 py-2 rounded-md transition font-semibold flex items-center"
                >
                  <Save className="mr-2" size={18} />
                  {editingId ? 'Update Payment' : 'Save Payment'}
                </button>
                <button 
                  type="button" 
                  onClick={handleCancel} 
                  className="bg-gray-600 hover:bg-gray-700 text-white px-6 py-2 rounded-md transition font-semibold flex items-center"
                >
                  <X className="mr-2" size={18} />
                  Cancel
                </button>
              </div>
            </form>
          </div>
        )}

        {/* Payment Table */}
        <div className="bg-white rounded-lg shadow-md overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gradient-to-r from-green-700 to-green-800 text-white">
                <tr>
                  <th className="px-4 py-3 text-left text-sm">#</th>
                  <th className="px-4 py-3 text-left text-sm">👤 Customer</th>
                  <th className="px-4 py-3 text-left text-sm">💳 Payment Type</th>
                  <th className="px-4 py-3 text-left text-sm">💰 Total Amount</th>
                  <th className="px-4 py-3 text-left text-sm">💵 Amount Paid</th>
                  <th className="px-4 py-3 text-left text-sm">📊 Remaining</th>
                  <th className="px-4 py-3 text-left text-sm">📅 Payment Date</th>
                  <th className="px-4 py-3 text-left text-sm">⚡ Actions</th>
                </tr>
              </thead>
              <tbody>
                {payments.length === 0 ? (
                  <tr>
                    <td colSpan="8" className="px-6 py-8 text-center text-gray-500">
                      No payments found. Click "Add Payment" to add your first payment!
                    </td>
                  </tr>
                ) : (
                  payments.map((payment, index) => (
                    <tr key={payment.id} className="border-b hover:bg-gray-50 transition">
                      <td className="px-4 py-3 text-gray-500 text-sm">{index + 1}</td>
                      <td className="px-4 py-3 text-gray-800 font-medium">{payment.customer_name}</td>
                      <td className="px-4 py-3">
                        <span className={`px-2 py-1 rounded-full text-xs font-medium flex items-center gap-1 ${getPaymentTypeColor(payment.payment_type)}`}>
                          {getPaymentTypeIcon(payment.payment_type)}
                          {payment.payment_type.replace('_', ' ')}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-gray-800 font-medium">₹{payment.total_amount?.toFixed(2)}</td>
                      <td className="px-4 py-3 text-green-600 font-semibold">₹{payment.amount_paid?.toFixed(2)}</td>
                      <td className="px-4 py-3">
                        <span className={`font-semibold ${
                          payment.remaining_amount > 0 ? 'text-red-600' : 'text-green-600'
                        }`}>
                          ₹{payment.remaining_amount?.toFixed(2)}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-gray-600">{payment.payment_date}</td>
                      <td className="px-4 py-3">
                        <div className="flex gap-2">
                          <button
                            onClick={() => handleEdit(payment)}
                            className="text-blue-600 hover:text-blue-800 transition p-1 hover:bg-blue-50 rounded"
                            title="Edit Payment"
                          >
                            <Edit2 className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => handleDelete(payment.id)}
                            className="text-red-600 hover:text-red-800 transition p-1 hover:bg-red-50 rounded"
                            title="Delete Payment"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Payments;